import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import BottomNav from '../../components/BottomNav'
import Spinner from '../../components/Spinner'
import { getCategories, getProducts } from '../../api/products'
import { useAuth } from '../../context/AuthContext'

const API = import.meta.env.VITE_API_URL ?? ''

const G = '#C89B4C'
const GB = 'rgba(200,155,76,0.14)'
const BD = 'rgba(244,238,225,0.14)'
const S = 'linear-gradient(155deg, rgba(255,248,235,0.05) 0%, rgba(255,248,235,0.015) 100%)'
const GLASS = { background: S, backdropFilter: 'blur(11px) saturate(150%)', WebkitBackdropFilter: 'blur(11px) saturate(150%)' } as const
const TX = '#F4F1EA'
const TX2 = '#C8C4BB'
const TX3 = '#8A8480'
const SERIF = "'Newsreader', Georgia, serif"

export default function CatalogPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const lang = user?.language ?? 'ru'
  const [search, setSearch] = useState('')
  const [categoryId, setCategoryId] = useState<number | undefined>()

  const { data: categories } = useQuery({ queryKey: ['categories'], queryFn: getCategories })
  const { data, isLoading } = useQuery({
    queryKey: ['products', categoryId, search],
    queryFn: () => getProducts({ category_id: categoryId, search: search || undefined }),
    placeholderData: (prev) => prev,
  })

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 40, padding: '10px 20px',
        background: 'rgba(13,21,38,0.82)', backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)', borderBottom: `1px solid ${BD}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, color: G, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
            ANSOR NASIYA
          </span>
          {data && (
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: TX3 }}>
              {data.total} {lang === 'uz' ? 'ta' : 'шт.'}
            </span>
          )}
        </div>

        {/* Search */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px', borderRadius: 12,
          background: S, border: `1px solid ${BD}`,
        }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={TX3} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/>
          </svg>
          <input
            style={{ background: 'transparent', border: 'none', outline: 'none', fontSize: 14, color: TX, width: '100%', fontFamily: "'Inter', sans-serif" }}
            placeholder={lang === 'uz' ? 'Qidirish...' : 'Поиск...'}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', color: TX3, cursor: 'pointer', padding: 0, fontSize: 16, lineHeight: 1 }}>×</button>
          )}
        </div>
      </header>

      {/* Category pills */}
      {categories && categories.length > 0 && (
        <div style={{
          display: 'flex', gap: 6, padding: '12px 20px 0', overflowX: 'auto',
          borderBottom: `1px solid ${BD}`, paddingBottom: 12,
        }}>
          <button
            onClick={() => setCategoryId(undefined)}
            style={{
              padding: '6px 14px', borderRadius: 99, whiteSpace: 'nowrap',
              fontSize: 12, fontWeight: 500,
              background: !categoryId ? G : 'transparent',
              color: !categoryId ? '#0D1526' : TX3,
              border: !categoryId ? 'none' : `1px solid ${BD}`,
              cursor: 'pointer',
            }}
          >
            {lang === 'uz' ? 'Barchasi' : 'Все'}
          </button>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setCategoryId(cat.id === categoryId ? undefined : cat.id)}
              style={{
                padding: '6px 14px', borderRadius: 99, whiteSpace: 'nowrap',
                fontSize: 12, fontWeight: 500,
                background: categoryId === cat.id ? G : 'transparent',
                color: categoryId === cat.id ? '#0D1526' : TX3,
                border: categoryId === cat.id ? 'none' : `1px solid ${BD}`,
                cursor: 'pointer',
              }}
            >
              {cat.emoji} {lang === 'uz' ? cat.name_uz : cat.name_ru}
            </button>
          ))}
        </div>
      )}

      <main style={{ flex: 1, padding: '16px 20px 104px' }}>
        {/* Page title */}
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 32, color: TX, margin: '0 0 6px', lineHeight: 1.1 }}>
            {lang === 'uz' ? 'Katalog' : 'Каталог'}
          </h1>
          <p style={{ margin: 0, fontSize: 12, color: TX3 }}>
            {lang === 'uz' ? "0%dan boshlang'ich to'lov" : 'рассрочка от 0% первый взнос'}
          </p>
        </div>

        {isLoading ? (
          <Spinner className="mt-10" />
        ) : !data?.items.length ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: TX3, fontSize: 14 }}>
            {lang === 'uz' ? 'Mahsulotlar yo\'q' : 'Нет товаров'}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            {data.items.map(p => {
              const name = lang === 'uz' ? p.name_uz : p.name_ru
              const catName = p.category ? (lang === 'uz' ? p.category.name_uz : p.category.name_ru) : null
              return (
                <div
                  key={p.id}
                  onClick={() => navigate(`/catalog/${p.id}`)}
                  style={{
                    ...GLASS, border: `1px solid ${BD}`,
                    borderRadius: 16, overflow: 'hidden', cursor: 'pointer',
                  }}
                >
                  {/* Image */}
                  <div style={{ height: 130, background: 'rgba(255,248,235,0.03)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
                    {p.image_path
                      ? <img src={`${API}/uploads/${p.image_path}`} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <span style={{ fontSize: 44 }}>📦</span>
                    }
                    {catName && (
                      <span style={{ position: 'absolute', top: 8, left: 8, fontSize: 9, fontWeight: 600, padding: '3px 8px', borderRadius: 99, background: 'rgba(13,21,38,0.70)', color: TX3, letterSpacing: '0.06em', backdropFilter: 'blur(4px)' }}>
                        {catName.toUpperCase()}
                      </span>
                    )}
                  </div>
                  {/* Info */}
                  <div style={{ padding: '10px 12px 12px' }}>
                    <p style={{ margin: '0 0 6px', fontSize: 13, color: TX, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {name}
                    </p>
                    <p style={{ margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: 14, color: G, fontWeight: 500 }}>
                      ${p.price.toLocaleString()}
                    </p>
                    <p style={{ margin: '4px 0 0', fontSize: 10, color: TX3 }}>
                      {lang === 'uz' ? "bo'lib to'lash" : 'в рассрочку'}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  )
}
